import { brightenOnDark, ensureContrast, mix } from './colorMath';
import type { Persona } from '@/personas/types';

export type ThemeTokens = {
  bg: string;
  card: string;
  primary: string;
  accent: string;
  secondaryBg: string;
  text: string;
  textSoft: string;
  border: string;
  warmAccent?: string;
  coolAccent?: string;
};

export type AppTheme = {
  id: string;
  name: string;
  emoji: string;
  light: ThemeTokens;
  dark: ThemeTokens;
};

const DARK_BG = '#121416';
const DARK_TEXT = '#F1F3F5';

/**
 * WCAG 1.4.3 kräver 4,5:1 för normal text. Grinden siktar något över, så att
 * avrundning i webbläsarens färghantering aldrig tippar ett par under kravet.
 */
const AA_TEXT = 4.6;

/**
 * Ytorna en textfärg faktiskt hamnar på: sidbakgrund, kort, sekundär yta och ett
 * primärtonat kort (chips, markerade kort och "tomt läge"-rutor blandar in temats
 * primary i kortet — paletteSync.surface tar 13 %, här räknas med lite mer).
 */
const textSurfaces = (t: Pick<ThemeTokens, 'bg' | 'card' | 'secondaryBg' | 'primary'>) => [
  t.bg,
  t.card,
  t.secondaryBg,
  mix(t.card, t.primary, 0.18),
];

/**
 * Flyttar `color` mot `toward` tills den når AA mot VARJE yta. Ytorna är
 * antingen alla ljusa (ljust läge, toward = mörk text) eller alla mörka (mörkt
 * läge, toward = vit), så ett steg som hjälper mot en yta försämrar aldrig en
 * annan — ordningen spelar ingen roll.
 */
const readableOn = (color: string, surfaces: string[], toward: string): string =>
  surfaces.reduce((c, surface) => ensureContrast(c, surface, AA_TEXT, toward), color);

/**
 * AA-grind för ljust läge. `primary` och `textSoft` används som TEXTFÄRG i hela
 * appen (rubriker, länkar, veckodagar, hjälptext) och `primary` dessutom som
 * yta under vit text (knappar, sidhuvud, "Idag"-märket). Flera teman låg långt
 * under kravet — Sun #E8A838 gav 2,08:1 mot vitt, Ocean:s textSoft 3,69:1 mot
 * sin bakgrund. Grinden mörkar dem mot temats egen text, så kulören bevaras och
 * ett tema som redan klarar kravet lämnas orört. Eftersom kortet är vitt ger
 * AA mot kortet också AA för vit text PÅ primary.
 *
 * `accent` rörs inte: den är en dekorfärg för ytor och ramar, inte för text.
 */
const accessibleLight = (light: ThemeTokens): ThemeTokens => {
  const primary = readableOn(light.primary, [light.bg, light.card, light.secondaryBg], light.text);
  const surfaces = textSurfaces({ ...light, primary });
  return {
    ...light,
    primary,
    textSoft: readableOn(light.textSoft, surfaces, light.text),
  };
};

/**
 * Mörkt läge härleds från temats ljusa färger.
 *
 * Tidigare blandades korten med 12 % av temats primary ovanpå en grå bas, och
 * primary ljusades genom att blandas mot vitt. Resultatet blev gråbruna och
 * grågröna ytor med urvattnade pastelltoner på — lågkontrastigt och grumligt,
 * särskilt i varma teman (espresso, sunset).
 *
 * Nu gäller tre principer:
 *  1. Ytorna är nästan neutrala. Temats kulör finns kvar som en ANING (3–6 %),
 *     så teman känns olika, men ytorna är kolsvarta och rena.
 *  2. Ytorna har tydliga steg: bg < secondaryBg < card < border. Kortet syns mot
 *     sidan, och ramen syns mot kortet.
 *  3. Accentfärgen ljusas i HSL med bevarad (eller lyft) mättnad tills den är
 *     läsbar mot alla ytor — klar och levande, inte pastell. Den siktar något
 *     över AA (5,2:1) för att kännas skarp, inte bara godkänd.
 *
 * card/secondaryBg/border är OPAKA hex (rgba() läckte igenom Paper, Dialog och
 * Menu, se paletteSync).
 */
const DARK_SURFACE_TINT = { bg: 0.03, secondaryBg: 0.045, card: 0.06, border: 0.1 };

const deriveDark = (light: ThemeTokens): ThemeTokens => {
  const tint = (base: string, amount: number) => mix(base, light.primary, amount);
  const bg = tint(DARK_BG, DARK_SURFACE_TINT.bg);
  const secondaryBg = tint(mix(DARK_BG, '#FFFFFF', 0.035), DARK_SURFACE_TINT.secondaryBg);
  const card = tint(mix(DARK_BG, '#FFFFFF', 0.07), DARK_SURFACE_TINT.card);
  const border = tint(mix(DARK_BG, '#FFFFFF', 0.17), DARK_SURFACE_TINT.border);
  const primary = brightenOnDark(light.primary, [bg, secondaryBg, card], 5.2);
  return {
    bg,
    card,
    primary,
    accent: light.accent,
    secondaryBg,
    text: DARK_TEXT,
    // Neutral, ljus sekundärtext (~8:1): skarp mot kolsvart, tydligt dämpad mot
    // DARK_TEXT. Grinden säkrar AA även mot primärtonade kort.
    textSoft: readableOn('#B8BFC8', textSurfaces({ bg, card, secondaryBg, primary }), '#FFFFFF'),
    border,
    warmAccent: light.warmAccent,
    coolAccent: light.coolAccent,
  };
};

/**
 * Bygger ett AppTheme: ljust läge går genom AA-grinden, och `dark` härleds från
 * temats ORIGINALfärger (inte de mörkade) så att mörkt läge behåller den ljusa
 * kulören som bas.
 */
const makeTheme = (base: Omit<AppTheme, 'dark'>): AppTheme => ({
  ...base,
  light: accessibleLight(base.light),
  dark: deriveDark(base.light),
});

// ─── CHILD THEMES (mjuka, lekfulla, varma) ───

export const CHILD_THEMES: AppTheme[] = [
  makeTheme({
    id: 'strawberry',
    name: 'settings.theme.strawberry',
    emoji: '🍓',
    light: {
      bg: '#FFF5F8',
      card: '#FFFFFF',
      primary: '#E8729A',
      accent: '#FFB3C6',
      secondaryBg: '#FFF0F4',
      text: '#2E1520',
      textSoft: '#8A5A6B',
      border: '#F5D4DE',
    },
  }),
  makeTheme({
    id: 'lavender',
    name: 'settings.theme.lavender',
    emoji: '💜',
    light: {
      bg: '#F8F5FF',
      card: '#FFFFFF',
      primary: '#9B72E8',
      accent: '#D4B3FF',
      secondaryBg: '#F3EEFF',
      text: '#1E1530',
      textSoft: '#6B5A8A',
      border: '#DDD0F5',
    },
  }),
  makeTheme({
    id: 'sky',
    name: 'settings.theme.sky',
    emoji: '☁️',
    light: {
      bg: '#F3F9FF',
      card: '#FFFFFF',
      primary: '#5BA4D9',
      accent: '#A8D8F0',
      secondaryBg: '#EAF4FD',
      text: '#12202E',
      textSoft: '#5A7A8A',
      border: '#C8E0F0',
    },
  }),
  makeTheme({
    id: 'clover',
    name: 'settings.theme.clover',
    emoji: '🍀',
    light: {
      bg: '#F2FBF6',
      card: '#FFFFFF',
      primary: '#4CAF82',
      accent: '#A8E6C3',
      secondaryBg: '#E8F7EF',
      text: '#122E1E',
      textSoft: '#5A8A6B',
      border: '#C4E8D4',
    },
  }),
  makeTheme({
    id: 'sun',
    name: 'settings.theme.sun',
    emoji: '☀️',
    light: {
      bg: '#FFFBF0',
      card: '#FFFFFF',
      primary: '#E8A838',
      accent: '#FFD97A',
      secondaryBg: '#FFF7E6',
      text: '#2E2212',
      textSoft: '#8A7A5A',
      border: '#F0E0C0',
    },
  }),
];

// ─── TEEN THEMES (häftigare, djupare, mer kontrast) ───

export const TEEN_THEMES: AppTheme[] = [
  makeTheme({
    id: 'neon',
    name: 'settings.theme.neon',
    emoji: '⚡',
    light: {
      bg: '#F0FDFF',
      card: '#FFFFFF',
      primary: '#06B6D4',
      accent: '#67E8F9',
      secondaryBg: '#E6FAFE',
      text: '#0C2D33',
      textSoft: '#4A8A95',
      border: '#B0E8F2',
    },
  }),
  makeTheme({
    id: 'lava',
    name: 'settings.theme.lava',
    emoji: '🌋',
    light: {
      bg: '#FFF5F3',
      card: '#FFFFFF',
      primary: '#DC4A2F',
      accent: '#FF8A6B',
      secondaryBg: '#FFEDE9',
      text: '#2E1510',
      textSoft: '#8A5A50',
      border: '#F5CCC4',
    },
  }),
  makeTheme({
    id: 'galaxy',
    name: 'settings.theme.galaxy',
    emoji: '🌌',
    light: {
      bg: '#F6F3FF',
      card: '#FFFFFF',
      primary: '#7C3AED',
      accent: '#A78BFA',
      secondaryBg: '#EDE8FF',
      text: '#1A1030',
      textSoft: '#6B508A',
      border: '#D4C4F5',
    },
  }),
  makeTheme({
    id: 'toxic',
    name: 'settings.theme.toxic',
    emoji: '☢️',
    light: {
      bg: '#F0FFF4',
      card: '#FFFFFF',
      primary: '#22C55E',
      accent: '#86EFAC',
      secondaryBg: '#E6FBEC',
      text: '#0C2E18',
      textSoft: '#4A8A5A',
      border: '#B0E8C4',
    },
  }),
  makeTheme({
    id: 'blueberry',
    name: 'settings.theme.blueberry',
    emoji: '🫐',
    light: {
      bg: '#F0F5FF',
      card: '#FFFFFF',
      primary: '#2563EB',
      accent: '#7CB3FF',
      secondaryBg: '#E6EEFF',
      text: '#101830',
      textSoft: '#506A8A',
      border: '#C0D4F5',
    },
  }),
];

// ─── ADULT THEMES (young + adult delar, lugna, mogna, harmoniska) ───

export const ADULT_THEMES: AppTheme[] = [
  makeTheme({
    id: 'forest',
    name: 'settings.theme.forest',
    emoji: '🌲',
    light: {
      bg: '#F4F8F0',
      card: '#FFFFFF',
      primary: '#2C8C08',
      accent: '#BBD981',
      secondaryBg: '#EDF5E8',
      text: '#1A2E12',
      textSoft: '#5A7A4E',
      border: '#D4E5CC',
      warmAccent: '#D3BA9C',
      coolAccent: '#B6D4DD',
    },
  }),
  makeTheme({
    id: 'ocean',
    name: 'settings.theme.ocean',
    emoji: '🌊',
    light: {
      bg: '#F0F8F7',
      card: '#FFFFFF',
      primary: '#279B95',
      accent: '#6E9AD4',
      secondaryBg: '#E8F4F3',
      text: '#122E2C',
      textSoft: '#4A8A86',
      border: '#A0C0E3',
    },
  }),
  makeTheme({
    id: 'sunset',
    name: 'settings.theme.sunset',
    emoji: '🌅',
    light: {
      bg: '#FDF8F4',
      card: '#FFFFFF',
      primary: '#C06030',
      accent: '#E8A86B',
      secondaryBg: '#FBF0E8',
      text: '#2E1A0E',
      textSoft: '#8A6248',
      border: '#F0DED0',
    },
  }),
  makeTheme({
    id: 'slate',
    name: 'settings.theme.slate',
    emoji: '🪨',
    light: {
      bg: '#F4F7FA',
      card: '#FFFFFF',
      primary: '#4A6380',
      accent: '#8AAFC4',
      secondaryBg: '#ECF0F4',
      text: '#1A2530',
      textSoft: '#5A7080',
      border: '#C4D4E0',
    },
  }),
  makeTheme({
    id: 'espresso',
    name: 'settings.theme.espresso',
    emoji: '☕',
    light: {
      bg: '#FAF6F2',
      card: '#FFFFFF',
      primary: '#7A5230',
      accent: '#C49A6C',
      secondaryBg: '#F5EDE6',
      text: '#2E1E12',
      textSoft: '#8A6A50',
      border: '#E0CFC0',
    },
  }),
];

// ─── Aggregated lookup ───

const ALL_THEMES: AppTheme[] = [...CHILD_THEMES, ...TEEN_THEMES, ...ADULT_THEMES];

const THEME_MAP: Record<string, AppTheme> = Object.fromEntries(ALL_THEMES.map((t) => [t.id, t]));

const DEFAULT_THEME_ID = 'forest';

// ─── Public API ───

export const getThemesForPersona = (persona: Persona): AppTheme[] => {
  switch (persona) {
    case 'child':
      return CHILD_THEMES;
    case 'teen':
      return TEEN_THEMES;
    case 'adult':
      return ADULT_THEMES;
    default:
      return ADULT_THEMES;
  }
};

/**
 * Personans FÖRSTA tema — används som fallback när ett lagrat tema tillhör en
 * annan persona (t.ex. ett barn-tema kvar i localStorage när personan blivit
 * teen). Ger child 'strawberry', teen 'neon', adult 'forest' (= DEFAULT_THEME_ID).
 */
export const getDefaultThemeForPersona = (persona: Persona): string =>
  getThemesForPersona(persona)[0].id;

export const getThemeTokens = (themeId: string, isDarkMode?: boolean): ThemeTokens => {
  const theme = THEME_MAP[themeId] ?? THEME_MAP[DEFAULT_THEME_ID];
  return isDarkMode ? theme.dark : theme.light;
};

export const getAppTheme = (themeId: string): AppTheme => {
  return THEME_MAP[themeId] ?? THEME_MAP[DEFAULT_THEME_ID];
};

/**
 * @deprecated Use getThemeTokens(themeId, isDarkMode) instead.
 * Kept for backward compatibility during migration.
 */
export const APP_THEMES: Record<
  string,
  { id: string; name: string; isDark: boolean; tokens: ThemeTokens }
> = Object.fromEntries(
  ALL_THEMES.map((t) => [t.id, { id: t.id, name: t.name, isDark: false, tokens: t.light }]),
);
